import React, { useState, useCallback } from 'react';
import { Small } from '@/components/Typography';
import { getFieldConfig } from '@/utils/pdfFieldConfig';

interface SidebarField {
  _id: number;
  field_type: string;
  page: number;
  sort_order: number;
}

interface PdfFieldSidebarProps {
  fields: SidebarField[];
  selectedFieldId: number | null;
  onSelectField: (fieldId: number, page: number) => void;
  onReorder: (reorderedFieldIds: number[]) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const PdfFieldSidebar: React.FC<PdfFieldSidebarProps> = ({
  fields,
  selectedFieldId,
  onSelectField,
  onReorder,
  collapsed,
  onToggleCollapse,
}) => {
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const sortedFields = [...fields].sort((a, b) => a.sort_order - b.sort_order);

  const handleDragStart = useCallback((e: React.DragEvent, fieldId: number) => {
    e.dataTransfer.setData('text/plain', String(fieldId));
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      setDragOverIndex(null);
      const draggedId = Number(e.dataTransfer.getData('text/plain'));
      const currentOrder = sortedFields.map((f) => f._id);
      const draggedIdx = currentOrder.indexOf(draggedId);
      if (draggedIdx === -1 || draggedIdx === dropIndex) return;

      currentOrder.splice(draggedIdx, 1);
      currentOrder.splice(dropIndex, 0, draggedId);
      onReorder(currentOrder);
    },
    [sortedFields, onReorder],
  );

  return (
    <div
      style={{
        position: 'relative',
        width: collapsed ? '36px' : '220px',
        minWidth: collapsed ? '36px' : '220px',
        transition: 'width 0.2s, min-width 0.2s',
        borderLeft: '1px solid #d6deeb',
        background: '#f8fafc',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: '8px',
          padding: collapsed ? '12px 4px' : '12px 12px 10px',
          borderBottom: '1px solid #d6deeb',
          background: '#f8fafc',
        }}
      >
        {!collapsed && (
          <div style={{ minWidth: 0 }}>
            <Small style={{ color: '#0f172a', fontWeight: 700 }}>
              Placed fields
            </Small>
            <Small style={{ color: '#64748b', display: 'block' }}>
              {fields.length} total
            </Small>
          </div>
        )}
        <button
          type="button"
          onClick={onToggleCollapse}
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '999px',
            border: '1px solid #d6deeb',
            background: '#fff',
            color: '#475569',
            cursor: 'pointer',
            padding: 0,
          }}
          title={collapsed ? 'Show fields panel' : 'Hide fields panel'}
        >
          {collapsed ? '\u25C0' : '\u25B6'}
        </button>
      </div>

      {!collapsed && (
        <div>
          <div
            style={{ overflowY: 'auto', maxHeight: '520px', padding: '10px' }}
          >
            {sortedFields.map((field, index) => {
              const config = getFieldConfig(field.field_type);
              const isSelected = field._id === selectedFieldId;
              return (
                <div
                  key={field._id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, field._id)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onClick={() => onSelectField(field._id, field.page)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    cursor: 'grab',
                    border: isSelected
                      ? `1px solid ${config.color}`
                      : '1px solid #d6deeb',
                    borderRadius: '14px',
                    marginBottom: '8px',
                    backgroundColor: isSelected
                      ? config.bgColorLight
                      : dragOverIndex === index
                        ? '#eff6ff'
                        : '#fff',
                    boxShadow: isSelected
                      ? '0 10px 24px rgba(15, 23, 42, 0.08)'
                      : '0 4px 12px rgba(15, 23, 42, 0.04)',
                    transition:
                      'background-color 0.15s, box-shadow 0.15s, border-color 0.15s',
                  }}
                >
                  <span
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: config.color,
                      color: '#fff',
                      fontSize: '10px',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Small
                      style={{
                        color: '#0f172a',
                        display: 'block',
                        fontWeight: 700,
                      }}
                    >
                      {config.label}
                    </Small>
                    <Small style={{ color: '#64748b' }}>
                      Page {field.page}
                    </Small>
                  </div>
                  <Small style={{ color: '#94a3b8', flexShrink: 0 }}>
                    #{index + 1}
                  </Small>
                </div>
              );
            })}
            {sortedFields.length === 0 && (
              <div
                style={{
                  padding: '18px 14px',
                  border: '1px dashed #cbd5e1',
                  borderRadius: '14px',
                  background: '#fff',
                }}
              >
                <Small style={{ color: '#64748b' }}>No fields added yet</Small>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfFieldSidebar;
